import { openPropertyReport, reportMoney } from "./propertyReport";
import { rulesReportSections } from "../components/PropertyRulesPanel";
import { getChargeAmounts } from "./chargeHelper";
import { resolveLatePolicy, describeLatePolicy } from "./latePolicy";

// What /find-public-property actually returns about the terms of a tenancy.
//
// The termination & violation rule-set lives behind /property-settings/:id,
// which is token-gated — a logged-out visitor gets a 401. So the public page
// works off the lease fields that *are* in the listing payload, plus the
// `is_property_settings` flag telling us a formal rule-set exists at all. It
// deliberately never claims "no terms" when the flag says otherwise.

const humanize = (value) =>
  String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

const ordinal = (day) => {
  const n = Number(day);
  if (!n) return humanize(day);
  if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][n % 10] || "th"}`;
};

export const isOn = (value) =>
  value === true || value === "true" || value === 1 || value === "1";

export function leaseTerms(flat = {}) {
  const latePolicy = resolveLatePolicy(flat);
  return [
    { label: "Lease duration", value: humanize(flat.leaseDuration) },
    { label: "Notice period", value: humanize(flat.noticePeriod) },
    {
      label: "Rent due",
      value: flat.rentDueDate ? `${ordinal(flat.rentDueDate)} of month` : "",
    },
    {
      // Stored as a bare percentage number, the same way the agreement
      // screen renders it.
      label: "Yearly rent increase",
      value: flat.rentIncrease ? `${flat.rentIncrease}%` : "",
    },
    {
      label: "Grace days before late fee",
      value: latePolicy.enabled && latePolicy.gracePeriodDays > 0
        ? `${latePolicy.gracePeriodDays} days`
        : "",
    },
    {
      label: "Late fee",
      value: describeLatePolicy(latePolicy) || "",
    },
  ].filter((row) => row.value);
}

export function moveInCosts(flat = {}) {
  const charges = getChargeAmounts(flat);
  return [
    { label: "Monthly rent", value: reportMoney(flat.flatRent) },
    { label: "Advance", value: reportMoney(flat.flatAdvance) },
    { label: "Security", value: reportMoney(charges.security) },
    { label: "Maintenance", value: reportMoney(charges.maintenance) },
    { label: "Trash", value: reportMoney(charges.trash) },
    { label: "Water", value: reportMoney(charges.water) },
  ];
}

export const moveInTotal = (flat = {}) => {
  const charges = getChargeAmounts(flat);
  return (
    Number(flat.flatRent || 0) +
    Number(flat.flatAdvance || 0) +
    charges.security +
    charges.maintenance +
    charges.trash +
    charges.water
  );
};

// `terms` is served by /find-public-property. A deployment where the client is
// ahead of the server simply has no such field — in that case fall back to the
// `is_property_settings` flag, which at least tells the visitor a rule-set
// exists, instead of wrongly claiming there are no terms.
export const hasTerms = (flat = {}) =>
  Boolean(flat.terms && Object.keys(flat.terms).length);

export function terminationSections(flat = {}) {
  if (hasTerms(flat)) return rulesReportSections(flat.terms);

  return [
    {
      heading: "Termination & violation terms",
      type: "text",
      text: flat.is_property_settings
        ? "This property has a registered termination and violation rule-set — notice periods, early-exit penalties, immediate-termination grounds and deposit-refund conditions. Sign in and open the property to read the full text."
        : "",
      empty:
        "The landlord has not registered a termination and violation rule-set for this property yet. Ask for the exit terms in writing before you commit.",
    },
  ];
}

export function houseRules(flat = {}) {
  const rules = (flat.landlordConditions || []).map(humanize);
  if (flat.customConditions) rules.push(flat.customConditions);
  return rules;
}

export const AMENITY_FIELDS = [
  ["petAllowed", "Pets allowed"],
  ["parkingAvailable", "Parking"],
  ["furnished", "Furnished"],
  ["pool", "Swimming pool"],
  ["bathTub", "Bath tub"],
  ["balcony", "Balcony"],
  ["elevator", "Elevator"],
  ["wifi", "WiFi"],
  ["gym", "Gym"],
  ["roof", "Roof access"],
  ["smokeAlarm", "Smoke alarm"],
  ["firstAidKit", "First aid kit"],
  ["fireExtinguisher", "Fire extinguisher"],
];

export const amenityList = (flat = {}) =>
  AMENITY_FIELDS.filter(([field]) => isOn(flat[field])).map(([, label]) => label);

/**
 * The whole listing as one saveable document — every figure, every term, every
 * rule. This is the answer to "the card cannot hold all of it".
 */
export function downloadPublicPropertyReport(flat, landlord = {}) {
  const rules = houseRules(flat);
  const amenities = amenityList(flat);
  const terms = leaseTerms(flat);

  const opened = openPropertyReport({
    title: flat.flatName || "Property",
    subtitle:
      flat.propertyAddress ||
      [flat.flatNumber, flat.city].filter(Boolean).join(", ") ||
      "Location not specified",
    accent: "#11998e",
    stats: [
      { label: "Monthly rent", value: reportMoney(flat.flatRent) },
      { label: "Move-in cost", value: reportMoney(moveInTotal(flat)) },
      {
        label: "Layout",
        value: `${flat.flatRooms || 0} bed • ${flat.flatToilet || 0} bath`,
      },
      {
        label: "Allowed for",
        value: humanize(flat.allowedFor) || "Any",
      },
    ],
    sections: [
      {
        heading: "Property",
        rows: [
          { label: "Name", value: flat.flatName },
          { label: "Flat number", value: flat.flatNumber },
          { label: "Floor", value: flat.flatFloor },
          { label: "Bedrooms", value: flat.flatRooms },
          { label: "Bathrooms", value: flat.flatToilet },
          { label: "Kitchens", value: flat.flatKitchen },
          { label: "Max occupants", value: flat.personAllowed },
          { label: "Allowed for", value: humanize(flat.allowedFor) },
          { label: "City", value: flat.city },
          { label: "Address", value: flat.propertyAddress || flat.mapAddress },
        ],
      },
      {
        heading: "Costs",
        note: `Move-in total (rent + advance + security): ${reportMoney(
          moveInTotal(flat)
        )}`,
        rows: moveInCosts(flat),
      },
      {
        heading: "Lease terms",
        rows: terms,
        empty: "The landlord has not published lease terms for this listing.",
      },
      ...terminationSections(flat),
      {
        heading: "House rules",
        type: "table",
        columns: ["#", "Rule"],
        empty: "No house rules were listed for this property.",
        rows: rules.map((rule, index) => [String(index + 1), rule]),
      },
      {
        heading: "Facilities",
        type: "table",
        columns: ["Facility", "Available"],
        rows: AMENITY_FIELDS.map(([field, label]) => [
          label,
          isOn(flat[field]) ? "Yes" : "No",
        ]),
        note: amenities.length
          ? `${amenities.length} of ${AMENITY_FIELDS.length} available`
          : "None listed",
      },
      {
        heading: "Listed by",
        rows: [
          { label: "Owner", value: landlord.name || "—" },
          { label: "Email", value: landlord.email || "—" },
        ],
      },
    ],
  });

  if (!opened) {
    window.alert(
      "Please allow pop-ups for this site to download the property report."
    );
  }
}
